import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppStore } from '@/store'
import { useApiFeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlag'
import { isFabricEmbedMode } from '@/lib/fabricEmbedAuth'
import { trackEvent } from '@/utils/analytics'
import { isSafeAppPath } from './protocol'
import HelpAgentLauncher from './HelpAgentLauncher'
import HelpAgentPanel from './HelpAgentPanel'
import TourOverlay from './TourOverlay'

/**
 * Cross-route mount for the help agent ("Akeera"). Lives in RootLayout
 * (like InAppMeetingPanel) so the widget and any running tour survive
 * navigation. Renders nothing unless ALL gates pass:
 *   1. `help_agent_enabled` PostHog flag (and not while flags load —
 *      avoids a flash on tenants where it's off),
 *   2. an authenticated session,
 *   3. a non-excluded route (login, embeds, auth callbacks, standalone
 *      surfaces),
 *   4. not Fabric-embed mode (belt and braces with the /embed/* check).
 */
/**
 * ALLOWLIST: Akeera only appears on the app's main (top-level) pages.
 * Everything else — viewers, editors, builders, wizards, pages with
 * their own chat (board canvas, AI Chat, DC chat, Knowledge Graph),
 * embeds, auth callbacks — is excluded by NOT being listed, so new deep
 * pages default to no Akeera. Exact-match on the pathname (sub-routes
 * like /workflow/:id or /data-catalog/match-entity don't inherit).
 */
export const HELP_AGENT_MAIN_ROUTES = [
  '/intelligent-board',
  '/data-catalog',
  '/digital-coworker',
  '/workflow',
  '/human-expert-knowledge',
  '/connectors',
  '/jobs',
  '/settings',
  '/account-details',
] as const

export function isHelpAgentExcludedRoute(pathname: string): boolean {
  const normalized =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
  return !(HELP_AGENT_MAIN_ROUTES as readonly string[]).includes(normalized)
}

/**
 * Surfaces where the widget must NEVER render, even mid-conversation:
 * login, externally-embedded views, auth callbacks, the standalone
 * meeting page. (Ordinary non-main routes only hide the LAUNCHER — an
 * already-open panel or running tour survives navigation there, so an
 * agent `navigate` directive doesn't unmount the answer mid-stream.)
 */
const HARD_EXCLUDED_PREFIXES = ['/embed/', '/auth/callback/', '/__test__/'] as const
// Exact-match excludes (no children): login and the standalone meeting page.
// Exact (not prefix) so a future '/meetings' or '/meeting-recap' isn't caught.
const HARD_EXCLUDED_EXACT = ['/', '/meeting'] as const

export function isHelpAgentHardExcluded(pathname: string): boolean {
  const normalized =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
  if ((HARD_EXCLUDED_EXACT as readonly string[]).includes(normalized)) return true
  return HARD_EXCLUDED_PREFIXES.some((prefix) => normalized.startsWith(prefix))
}

const HelpAgentRoot = () => {
  const { enabled, loading } = useApiFeatureFlag('help_agent_enabled', false)
  // Local-dev escape hatch (PostHog is disabled in dev) — pairs with the
  // backend's FEATURE_FLAG_OVERRIDES={"help_agent_enabled":true} env.
  const envEnabled = useFeatureFlag('VITE_HELP_AGENT_ENABLED')
  const userLogged = useAppStore((s) => s.userLogged)
  const helpStage = useAppStore((s) => s.helpStage)
  const helpActiveTour = useAppStore((s) => s.helpActiveTour)
  const helpPendingNavigation = useAppStore((s) => s.helpPendingNavigation)
  const openHelpAgent = useAppStore((s) => s.openHelpAgent)
  const consumeHelpNavigation = useAppStore((s) => s.consumeHelpNavigation)
  const location = useLocation()
  const navigate = useNavigate()

  // Act on `navigate` directives from the agent. Path safety is enforced
  // at parse time AND here (defense in depth — the slice is reachable
  // from devtools).
  useEffect(() => {
    if (!helpPendingNavigation) return
    if (isSafeAppPath(helpPendingNavigation) && helpPendingNavigation !== location.pathname) {
      navigate(helpPendingNavigation)
    }
    consumeHelpNavigation()
  }, [helpPendingNavigation, navigate, consumeHelpNavigation, location.pathname])

  const flagEnabled = envEnabled || (!loading && enabled)
  if (!flagEnabled || !userLogged) return null
  if (isHelpAgentHardExcluded(location.pathname) || isFabricEmbedMode()) return null

  // The LAUNCHER appears on main pages only; an already-open panel or a
  // running tour stays mounted across any (non-hard-excluded) route so
  // agent-driven navigation never unmounts a streaming answer.
  const onMainPage = !isHelpAgentExcludedRoute(location.pathname)

  return (
    <>
      {onMainPage && helpStage === 'closed' && !helpActiveTour && (
        <HelpAgentLauncher
          onOpen={() => {
            trackEvent('help_agent-opened', { route: location.pathname })
            openHelpAgent()
          }}
        />
      )}
      {helpStage !== 'closed' && <HelpAgentPanel />}
      {helpActiveTour && <TourOverlay />}
    </>
  )
}

export default HelpAgentRoot
